<template>
  <FormContainer withoutDivider>
    <template #title>
      Liste des module(s) de formation
      <BasicAlert
        v-if="metaModuleEnCours?.$messageErreurDirty!"
        :alertLabel="metaModuleEnCours?.$messageErreurDirty!"
      />
    </template>
    <template #header-actions>
      <BasicButton
        variant="outlined"
        :label="!nbMaxModulesMessage ? 'Ajouter un module de formation' : 'Nombre maximum de modules atteint'"
        :icon-name="!nbMaxModulesMessage ? 'add' : 'danger'"
        @click="
          typeContextCourant === TypesContextesAutorisesEnum.Adherent &&
          model?.sousDispositif !== 'Fse' &&
          model?.sousDispositif !== 'FTJ'
            ? (afficherModale = true)
            : ajouterModule('Externe')
        "
        :disabled="!!nbMaxModulesMessage || readonly"
      />
    </template>
    <template #message>
      <BasicAlert
        v-if="nbMaxModulesMessage"
        alert-type="info"
        :alert-label="nbMaxModulesMessage"
        style="margin: -16px 0"
      />
    </template>
    <template
      #content
      v-if="listeModules && listeModules.length"
    >
      <!-- Desktop version -->
      <div class="elements--desktop">
        <div class="cardLayoutWrapper cardLayoutWrapper--header">
          <div class="elem elem--span-2"></div>
          <div class="elem elem--span-3">
            <span>Type</span>
          </div>
          <div class="elem elem--span-5">
            <span>Organisme</span>
          </div>
          <div class="elem elem--span-5">
            <span>Intitulé</span>
          </div>
          <div class="elem elem--span-4">
            <span>Début</span>
          </div>
          <div class="elem elem--span-4">
            <span>Fin</span>
          </div>
          <div class="elem elem--span-3">
            <BasicText wrap-all>Durée (h)</BasicText>
          </div>
          <div class="elem elem--span-4">
            <span>Modalité</span>
          </div>
          <div class="elem elem--span-3">
            <span>Subrogation</span>
          </div>
          <div class="elem elem--span-3">
            <span>Coûts</span>
          </div>
        </div>

        <div
          v-for="(module, index) in listeModules"
          class="listCardDesktopWrapper"
          :key="index"
        >
          <div
            class="cardLayoutWrapper cardLayoutWrapper--selectionnable"
            :class="{ isActive: activeModuleIndex === index }"
            @click="définirModuleEnCours(index)"
          >
            <BasicAlert
              :alert-type="!meta.formation?.modules?.at(index)?.$estErreur ? 'success' : 'danger'"
              :has-bg="false"
              class="elem elem--span-2"
            />
            <div class="elem elem--span-3">
              <span>{{ module.typeModule }}</span>
            </div>
            <div class="elem elem--span-5">
              <BasicText nb-max-lines="2">{{ module.etablissement?.denomination }}</BasicText>
            </div>
            <div class="elem elem--span-5">
              <BasicText nb-max-lines="2">{{ module.intitule }}</BasicText>
            </div>
            <div class="elem elem--span-4">
              <span>{{ dateFormatter(module.dateDebut) }}</span>
            </div>
            <div class="elem elem--span-4">
              <span>{{ dateFormatter(module.dateFin) }}</span>
            </div>
            <div class="elem elem--span-3">
              <span>{{ module.duree }}</span>
            </div>
            <div class="elem elem--span-4">
              <span>
                {{ module.modalitePedagogique ? ModalitePedagogiqueLibelle[module.modalitePedagogique] : '' }}
              </span>
            </div>
            <div class="elem elem--span-3">
              <span>{{ getLabelBoolean(module.avecSubrogation!) }}</span>
            </div>
            <div class="elem elem--span-3">
              <span>{{ sumMontantTotalFrais(module) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile version -->
      <div class="elements--mobile">
        <div
          v-for="(module, index) in listeModules"
          :key="index"
          :class="['card', { 'card--active': activeModuleIndex === index }]"
          @click="définirModuleEnCours(index)"
        >
          <div class="card__header">
            <BasicText weight="bold">Module n°{{ index + 1 }}</BasicText>
          </div>
          <div class="card__content">
            <BasicText wrap>
              <BasicText weight="bold">Type :</BasicText>
              {{ module.typeModule }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Organisme :</BasicText>
              {{ module.etablissement?.denomination }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Intitulé :</BasicText>
              {{ module.intitule }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Date de début :</BasicText>
              {{ dateFormatter(module.dateDebut) }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Date de fin :</BasicText>
              {{ dateFormatter(module.dateFin) }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Durée (h) :</BasicText>
              {{ module.duree }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Modalité pédagogique :</BasicText>
              {{ module.modalitePedagogique ? ModalitePedagogiqueLibelle[module.modalitePedagogique] : '' }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Subrogation :</BasicText>
              {{ getLabelBoolean(module.avecSubrogation!) }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Total des coûts demandés :</BasicText>
              {{ sumMontantTotalFrais(module) }}
            </BasicText>
          </div>
        </div>
      </div>
    </template>
    <template
      #content
      v-else
    >
      <!-- Empty state -->
      <BasicAlert
        v-if="meta.formation?.modules && meta.formation?.modules.$estErreurDirty"
        :alertLabel="meta.formation?.modules.$messageErreurDirty!"
      />
      <EmptyDataTable>
        <template #content>
          Retrouvez ici la
          <BasicText>liste des modules de formation que vous avez ajoutés</BasicText>
          (jusqu'à {{ compteursMax.nbMaxModules }} maximum). Vous devez en ajouter un au minimum.
        </template>
      </EmptyDataTable>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { inject } from 'vue'
  import { storeToRefs } from 'pinia'
  import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
  import { sumMontantFrais } from '@/features/domaine/regles-akto/shared'
  import { EmptyDataTable, FormContainer } from '@/features/interface/demande/shared'
  import { getLabelBoolean } from '@/features/interface/shared/tools'
  import { dateFormatter } from '@/features/shared/tools'
  import type { ModuleDTO } from '@/features/api/extranet/dto/Demande/AF'
  import { useDemandeAFStore } from '../../../../useDemandeAFStore'
  import { AF_MODULES_ACTIONS } from '../../useModuleActions'
  import { ModalitePedagogiqueLibelle } from '@/features/domaine/types/opco/Demande/AF'
  import { useNombreMaxSalariesModules } from '@/features/interface/demande/shared/composables'

  const store = useDemandeAFStore()
  const { nbMaxModulesMessage, compteursMax } = useNombreMaxSalariesModules(store)

  const { meta, typeContextCourant, readonly, model } = storeToRefs(store)

  const { afficherModale, activeModuleIndex, listeModules, définirModuleEnCours, metaModuleEnCours, ajouterModule } =
    inject(AF_MODULES_ACTIONS)!

  function sumMontantTotalFrais(module: ModuleDTO) {
    return sumMontantFrais(module.fraisPedagogiques!) + sumMontantFrais(module.fraisAnnexesSalarie!)
  }
</script>
